import {
	AdditionalQuestionsData,
	AppUserProfile,
	AttendanceImportTableItem,
	Constant,
	FieldConfig,
	ItemWithLocation,
	ItemWithOwnerCenter,
	LastModifiedInfo,
	RepeatDayOfWeekOptions,
	RepeatWeekOptions,
	TimeslotRecurrenceMonthlyMode,
	TimeslotRecurrenceType,
	Volunteer,
	Weekday,
} from '.';
import {
	NEUTRAL_CHIP,
	ACTIVE_CHIP,
	INACTIVE_CHIP,
	NEGATIVE_CHIP,
} from '../mixins/style/chip-mixins';
import {
	FormatDateTime,
	INTERNAL_DATE_FORMAT,
} from '../mixins/format-date-time';
import { MultiLangText } from './locale';
import { MultiStepFormValidationStatus } from './multi-step-form';

export type JobEventType = Constant;

export type VolunteerFunction = Constant;

export type JobSessionType = Constant;

export enum JobStatusCode {
	draft = 'draft',
	published = 'published',
	completed = 'completed',
	closed = 'closed',
	cancelled = 'cancelled',
	full = 'full',
}

export interface JobBasicInfo {
	eventType: string;
	causes: string[];
	recipients: string[];
	sdgs: string[];
}

export interface VolunteerInfo {
	volunteerFunctions: string[];
	skills: string[];
	languages: string[];
	languageOther: string | null;
	minAge: number | null;
	maxAge: number | null;
}

export enum JobVisibility {
	public = 'public',
	unlisted = 'unlisted',
	members = 'membersOnly',
	publicSuc = 'publicSuc',
}

export enum ApplicationRestriction {
	members = 'membersOnly',
	partners = 'partnersOnly',
	private = 'private',
	none = 'none',
	inviteMembers = 'inviteMembers', // not really a restriction, but show as restriction field for FE
}

export import JobValidationStatus = MultiStepFormValidationStatus;

export enum JobAttendanceRuleCode {
	optional = 'optional',
	must = 'must',
	atMost = 'atMost',
	atLeast = 'atLeast',
}

export enum JobEditStep {
	basicInfo = 'basicInfo',
	sessions = 'sessions',
	schedule = 'schedule',
	locationAndVacancy = 'locationAndVacancy',
	attendanceRule = 'attendanceRule',
	jobDetails = 'jobDetails',
	volunteerTarget = 'volunteerTarget',
	applicationSettings = 'applicationSettings',
	otherSettings = 'otherSettings',
}

export const JOB_STEPS = [
	JobEditStep.basicInfo,
	JobEditStep.sessions,
	JobEditStep.schedule,
	JobEditStep.locationAndVacancy,
	JobEditStep.attendanceRule,
	JobEditStep.jobDetails,
	JobEditStep.volunteerTarget,
	JobEditStep.applicationSettings,
	JobEditStep.otherSettings,
];

export type JobValidations = Partial<Record<JobEditStep, JobValidationStatus>>;

export interface JobTimeslot extends ItemWithLocation {
	id?: string; //it is the timeslot_instance id
	timeslotId?: string;
	position: number;
	dateGroupId: number;
	sessionId?: string;
	session?: JobSession;
	// Date
	isSingleDay: boolean;
	isDateTBC: boolean;
	startDate: string | null;
	endDate: string | null;
	// Time Slot
	isAllDay: boolean;
	isTimeTBC: boolean;
	startTime?: string | null;
	endTime?: string | null;
	// recurrence
	recurrenceType?: TimeslotRecurrenceType;
	recurrenceFrequency?: number;
	recurrenceWeekdays?: Weekday[];
	recurrenceMonthlyMode?: TimeslotRecurrenceMonthlyMode;
	recurrenceRepeatDayOfMonth?: number[];
	recurrenceRepeatWeek?: RepeatWeekOptions;
	recurrenceRepeatDayOfWeek?: RepeatDayOfWeekOptions;
	recurrenceExceptionDates?: string[];

	quota: number | null;
	volunteerCount?: number;
	usedQuota?: number;
	isMain?: boolean; // for internal job
	pendingQuota?: number;
	acceptedQuota?: number;
	createdAt?: Date;
	updatedAt?: Date;
}

export interface JobTimeslotFactoryInput
	extends Omit<JobTimeslot, 'startDate' | 'endDate' | 'startTime' | 'endTime'> {
	// undefined is different to null. undefined field will be overwritten, but null will not.
	startDate?: string | null;
	endDate?: string | null;
	startTime?: string | null;
	endTime?: string | null;
}

/**
 * generate default date and time
 *
 * @param timeslot
 * @returns
 */
export function defaultTimeSlotFactory(
	timeslot: JobTimeslotFactoryInput,
): JobTimeslot {
	const {
		startDate,
		startTime,
		endDate,
		endTime,
		isDateTBC,
		isSingleDay,
		isTimeTBC,
		isAllDay,
	} = timeslot;

	return {
		...timeslot,
		// Date
		startDate:
			startDate === undefined
				? isDateTBC
					? null
					: FormatDateTime.formatDate(new Date(), INTERNAL_DATE_FORMAT)
				: startDate,
		endDate:
			endDate === undefined
				? isDateTBC || isSingleDay
					? null
					: // one day later from now
					  FormatDateTime.formatDate(
							new Date(Date.now() + 24 * 36e5),
							INTERNAL_DATE_FORMAT,
					  )
				: endDate,

		// Time
		startTime:
			startTime === undefined
				? isTimeTBC
					? null
					: // next integer hour
					  FormatDateTime.formatTime(new Date(Date.now() + 36e5), 'HH:00')
				: startTime,
		endTime:
			endTime === undefined
				? isTimeTBC || isAllDay
					? null
					: // one hour later than startTime
					  FormatDateTime.formatTime(new Date(Date.now() + 36e5 * 2), 'HH:00')
				: endTime,
	};
}

export const OtherReasonCode = 'others';

export const AvailableAbsentReasonCodes = [
	'clashOfSchedule',
	'workCommitment',
	OtherReasonCode,
];
export interface JobAttendance {
	id: string;
	user?: Volunteer;
	userId: string;
	jobId: string;
	timeslotId: string;
	timeslotInstanceId: string;
	startDate?: string;
	appliedTimeslot?: JobAppTimeslot;
	appliedTimeslotId: string;
	checkInAt?: Date;
	checkOutAt?: Date;
	calculatedHours: number | null;
	confirmedHours?: number;
	confirmed: boolean;
	absent: boolean;
	absentReason: string | null;
	createdAt: Date;
	updatedAt: Date;
	remarks?: string | null;
}

export enum TimeslotProgressCode {
	scheduled = 'scheduled',
	ongoing = 'ongoing',
	completed = 'completed',
	cancelled = 'cancelled',
}

export interface JobTimeslotInstance {
	id: string;
	timeslot?: JobTimeslot;
	timeslotId: string;
	// Date
	isSingleDay?: boolean;
	isDateTBC?: boolean;
	startDate?: string;
	endDate?: string;
	// Time Slot
	isAllDay: boolean;
	isTimeTBC?: boolean;
	startTime?: string;
	endTime?: string;
	progress?: TimeslotProgressCode;
	confirmPendingCount: number;
}

export enum ScheduleFrequency {
	oneOff = 'oneOff',
	regular = 'regular',
}

export interface JobSession {
	id?: string;
	type: string;
	title: MultiLangText;
	position: number;
	job?: Job;
	jobId?: string;
	frequency?: ScheduleFrequency;
	timeslots?: JobTimeslot[];
	attendanceRule?: JobAttendanceRuleCode;
	attendanceRuleAmount?: number;
	createdAt?: Date;
	updatedAt?: Date;
}

export enum JobProgressCode {
	ongoing = 'ongoing',
	completed = 'completed',
}

export interface JobDto extends ItemWithOwnerCenter {
	id?: string;
	ngoId: number;
	status: JobStatusCode;

	// Basic information
	title: MultiLangText;
	requisitionNumber?: string;
	basicInfo: Partial<JobBasicInfo>;
	isInternal?: boolean;
	serviceStart?: Date | string;
	serviceEnd?: Date | string;

	// Job Sessions
	sessions?: JobSession[];

	// Job details
	expectedCommitHours: number;
	description: MultiLangText;
	remarks: MultiLangText;
	keywords: string[];
	imageUrls: string[];

	// Volunteer target
	volunteerInfo: Partial<VolunteerInfo>;
	parentalConsent?: boolean;
	pcAge?: number | null;
	quota: number;
	remainingQuota?: number;
	acceptedQuota?: number;
	pendingQuota?: number;
	groupApply?: boolean | null;
	applicationStart: Date | string | null;
	applicationEnd: Date | string | null;
	additionalQuestions?: FieldConfig[];
	visibility?: JobVisibility | null;
	applicationRestrictions: ApplicationRestriction[] | null;
	inviteMembers: boolean | null;
	feeDetail?: MultiLangText | null;
	contact?: string;
	contactEmail?: string;
	contactPhone?: string;

	// calculated fields
	pendingApplications?: number;
	cancelReason?: string;

	// Other settings
	autoApprove?: boolean;
	overallStart?: Date | string;
	overallEnd?: Date | string;

	// notification emails
	mainNotificationEmail: string;
	extraNotificationEmails?: string[];

	validations?: JobValidations;
	progress?: JobProgressCode;

	// share link
	jobLink?: string;

	// program related
	addedToReports?: string[];

	createdAt?: Date;
	updatedAt?: Date;
}

export interface Job extends ItemWithOwnerCenter {
	id: string;
	ngoId: number;
	status: JobStatusCode;

	// Basic information
	title: MultiLangText;
	requisitionNumber?: string;
	basicInfo: JobBasicInfo;
	isInternal: boolean;

	// Job Sessions
	sessions?: JobSession[];

	// Job details
	expectedCommitHours: number;
	description: MultiLangText;
	remarks: MultiLangText;
	keywords: string[];
	imageUrls: string[];

	// Volunteer target
	volunteerInfo: VolunteerInfo;
	parentalConsent: boolean;
	pcAge: number;
	quota: number;
	remainingQuota: number;
	acceptedQuota: number;
	pendingQuota: number;
	groupApply?: boolean;

	// application settings
	applicationStart: Date;
	applicationEnd: Date;
	additionalQuestions?: FieldConfig[];
	visibility?: JobVisibility;
	applicationRestrictions: ApplicationRestriction[] | null;
	inviteMembers: boolean | null;
	feeDetail: MultiLangText;
	contact: string;
	contactEmail: string;
	contactPhone: string;

	// calculated fields
	acceptedApplications?: number;
	pendingApplications?: number;

	//service hours
	progress?: JobProgressCode;
	sessionCompleted: number;
	timeslotConfirmed: number;
	timeslotTotal: number;

	// Other settings
	autoApprove: boolean;

	cancelReason: string;

	overallStart: Date;
	overallEnd: Date;
	serviceStart: Date;
	serviceEnd: Date;

	// program related
	addedToReports?: string[];

	validations?: JobValidations;

	// share link
	jobLink?: string;

	// notification emails
	mainNotificationEmail: string;
	extraNotificationEmails?: string[];

	createdAt: Date;
	updatedAt: Date;
	lastModifiedInfo: LastModifiedInfo;
}

export enum JobApplicationStatus {
	pending = 'pending',
	accepted = 'accepted',
	reserved = 'reserved',
	declined = 'declined',
	onHold = 'onHold',
	cancelled = 'cancelled',
}

export interface JobAppTimeslot {
	id?: string;
	jobApplication?: JobApplication;
	jobApplicationId: string;
	timeslotId: string;
	status: JobApplicationStatus;
	createdAt?: Date;
	updatedAt?: Date;
}

export interface JobApplicationParentInfo {
	email: string;
	name: string;
	relationship?: string;
	acceptCode?: string;
	declineCode?: string;
}

export interface JobApplicationStatusesCount {
	acceptedCount: number;
	reservedCount: number;
	declinedCount: number;
}

export interface JobApplication {
	id: string;
	status: JobApplicationStatus;
	jobId: string;
	ngoId: number;
	user?: AppUserProfile;
	userId: string;
	firstName?: string;
	lastName?: string;
	chineseName?: string;
	mobileNumber?: string;
	email?: string;
	tnc?: string[];
	additionalQuestions?: AdditionalQuestionsData[];
	// Parental Consent
	parentalConsent: boolean;
	parentInfo?: JobApplicationParentInfo;
	appliedTimeslots?: JobAppTimeslot[];
	timeslotStatusCnt?: JobApplicationStatusesCount;
	data: any;
	remarks?: string;
	reason: string | null;

	createdAt?: Date;
}

export interface JobAppUpdateTimeslotDto {
	id?: string;
	status?: JobApplicationStatus;
}

export interface JobApplicationUpdateDto {
	status?: JobApplicationStatus;
	firstName?: string;
	lastName?: string;
	chineseName?: string;
	mobileNumber?: string;
	email?: string;
	appliedTimeslots?: JobAppUpdateTimeslotDto[];
	timeslots?: JobAppTimeslotsDto;
	additionalQuestions?: AdditionalQuestionsData[];
	parentInfo?: JobApplicationParentInfo;
	remarks?: string;
	reason?: string | null;
	isBulk?: boolean;
}

export interface JobAppTimeslotsDto {
	[sessionKey: string]: string[];
}

export interface JobApplicationDto {
	id?: string;
	jobId: string;
	userId?: string;
	firstName?: string;
	lastName?: string;
	chineseName?: string;
	mobileNumber?: string;
	email?: string;
	timeslots: JobAppTimeslotsDto;
	isWalkIn?: boolean;
	isBulk?: boolean;
}

export const AvailableDeclineReasonCodes = [
	'fullQuota',
	'applicantUnreachable',
	'failedRequirement',
	'withdrawn',
	'completedOrPostponed',
	'serviceExpired',
	OtherReasonCode,
];

export const AvailableCancelReasonCodes = [
	'clashOfSchedule',
	'submitByMistake',
	OtherReasonCode,
];

export interface JobQrCodeCheckIn {
	id: string;
	time: string | Date;
}

export type JobOfflineAttendanceChange = Pick<JobAttendance, 'id'> &
	Partial<
		Pick<
			JobAttendance,
			| 'checkInAt'
			| 'checkOutAt'
			| 'confirmed'
			| 'confirmedHours'
			| 'absent'
			| 'absentReason'
			| 'calculatedHours' // for easier display, and will be re-calculated in BE
		>
	>;

export type JobGroupedAttendanceChanges = {
	[timeslotInstanceId: string]: JobOfflineAttendanceChange[];
};

export interface JobAttendanceImportTableItem
	extends AttendanceImportTableItem {
	checkInAt?: Date;
	checkOutAt?: Date;
}

export enum DateFilter {
	upcoming = 'upcoming',
	previous = 'previous',
}

export const JobProgressChipStyle = {
	[JobProgressCode.ongoing]: ACTIVE_CHIP,
	[JobProgressCode.completed]: INACTIVE_CHIP,
};

export const TimeslotProgressChipStyle = {
	[TimeslotProgressCode.completed]: INACTIVE_CHIP,
	[TimeslotProgressCode.ongoing]: ACTIVE_CHIP,
	[TimeslotProgressCode.scheduled]: NEUTRAL_CHIP,
	[TimeslotProgressCode.cancelled]: NEGATIVE_CHIP,
};

export const JobStatusChipStyle = {
	[JobStatusCode.draft]: NEUTRAL_CHIP,
	[JobStatusCode.published]: ACTIVE_CHIP,
	[JobStatusCode.completed]: INACTIVE_CHIP,
	[JobStatusCode.closed]: NEGATIVE_CHIP,
	[JobStatusCode.cancelled]: NEGATIVE_CHIP,
	[JobStatusCode.full]: ACTIVE_CHIP,
};

export const JobApplicationStatusChipStyle = {
	[JobApplicationStatus.pending]: NEUTRAL_CHIP,
	[JobApplicationStatus.accepted]: ACTIVE_CHIP,
	[JobApplicationStatus.reserved]: NEUTRAL_CHIP,
	[JobApplicationStatus.declined]: NEGATIVE_CHIP,
	[JobApplicationStatus.onHold]: NEUTRAL_CHIP,
	[JobApplicationStatus.cancelled]: NEGATIVE_CHIP,
};

export interface JobApplicationCache {
	[appUserId: string]: { [applicationId: string]: JobApplication };
}
